import * as THREE from 'three'
import type { AudioFrame, VisualParams } from '../types'
import { MAX_ZONES } from './shaderChunks'

/** Uniforms shared by all primitives, fed by the audio. */
export interface SharedUniforms {
  uTime: { value: number }
  /** Time that advances FASTER with the music's energy (accumulated by Scene).
   *  Use it for motion that should speed up on loud/beat parts without phase jumps. */
  uEnergyTime: { value: number }
  /** Reliable 0..1 pulse punched on every BPM-grid beat and decayed (never
   *  saturates like the audio beat detector). Use for steady on-beat reactions. */
  uBeatPulse: { value: number }
  uBass: { value: number }
  uMid: { value: number }
  uTreble: { value: number }
  uLevel: { value: number }
  uBeat: { value: number }
  uHue: { value: number }
  uReactivity: { value: number }
  uBassPunch: { value: number }
  /** Deformation seed as a 3D noise/phase offset (0,0,0 = canonical look). */
  uSeed: { value: THREE.Vector3 }
  /**
   * Beat pulse (0..1) that drives the seed-lobe deformation. Generated in Scene
   * from the BPM grid (keeps firing) scaled by energy (contracts on silence),
   * instead of the saturating audio beat detector.
   */
  uSeedPulse: { value: number }
  /** Global multiplier for the point size (slider). */
  uPointScale: { value: number }
  /** Point sprite shape: 0 circle, 1 square, 2 triangle. */
  uPointShape: { value: number }
  /** 1 = opaque points (hard edge), 0 = translucent glow. */
  uPointSolid: { value: number }
  // --- Video displacement field ('videodisplace' primitive) ---
  /** Displacement magnitude. */
  uDispScale: { value: number }
  /** Luminance midpoint (0..1): above pushes out, below sinks in. */
  uDispThreshold: { value: number }
  /** Audio modulation amount for the displacement. */
  uDispAudio: { value: number }
  /** Audio band driving it: 0 level, 1 bass, 2 mid, 3 treble. */
  uDispBand: { value: number }
  /** Renderer's devicePixelRatio (gl_PointSize is in framebuffer px). */
  uPixelRatio: { value: number }
  /** 1D texture with the frequency spectrum (R = magnitude 0..1). */
  uFreq: { value: THREE.DataTexture }
  uFreqSize: { value: number }
  /** 1D texture with the waveform (R = sample remapped 0..1, center 0.5). */
  uWave: { value: THREE.DataTexture }
  uWaveSize: { value: number }
  /** Selected media (video/image) for LED-screen-style primitives. */
  uMedia: { value: THREE.Texture }
  /** Second copy of the background video (offset) for the crossfade loop. */
  uMedia2: { value: THREE.Texture }
  /** Crossfade weight (0 = show uMedia, 1 = show uMedia2) for the seamless loop. */
  uLoopFade: { value: number }
  /** 1 if there is active media, 0 if not. */
  uMediaActive: { value: number }
  /** 1 = opaque fullscreen overlay with "cover" (primitive off). */
  uMediaFull: { value: number }
  /** Video/image aspect ratio (width/height), for the fullscreen "cover". */
  uMediaAspect: { value: number }
  /** Viewport aspect ratio (width/height). */
  uScreenAspect: { value: number }
  /** 1 = render the flat background as an infinite inward tunnel. */
  uTunnel: { value: number }
  /** Tunnel angular offset (rad): the camera's horizontal orbit spins the tunnel. */
  uTunnelAngle: { value: number }
  /** Tunnel depth offset: dollying the camera scrolls through the tunnel. */
  uTunnelDepth: { value: number }
  /** Tunnel screen shift: looking up/down/around pans the vanishing point. */
  uTunnelShift: { value: THREE.Vector2 }
  /** Mirror effect: 0 none, 1 horizontal, 2 vertical, 3 quad, 4 kaleidoscope. */
  uMirror: { value: number }
  /** Mirror density multiplier (folds / kaleidoscope segments). */
  uMirrorAmount: { value: number }
  /** Background video opacity (0..1). */
  uMediaOpacity: { value: number }
  /** 1 = the texture fills the primitive's pixels (screen-space). */
  uTexturePrimitive: { value: number }
  /** Drawing-buffer resolution (px), for screen-space texture sampling. */
  uResolution: { value: THREE.Vector2 }
  /** Texture painted inside the primitive — independent from the background media. */
  uPrimitiveMedia: { value: THREE.Texture }
  /** 1 if the primitive texture is loaded. */
  uPrimitiveMediaActive: { value: number }
  /** Aspect ratio (w/h) of the primitive texture, for the "cover" fit. */
  uPrimitiveAspect: { value: number }
  /** Palette LUT (256×1) for recoloring the primitive texture (Colors). */
  uReLUT: { value: THREE.Texture }
  /** 1 = recolor the primitive texture with the palette. */
  uReActive: { value: number }
  /** Gray↔color blend for the primitive recolor. */
  uReIntensity: { value: number }
  /** Black point of the primitive recolor levels. */
  uReBlack: { value: number }
  /** White point of the primitive recolor levels. */
  uReWhite: { value: number }
  /** Blend mode: 0 normal, 1 multiply, 2 screen, 3 add. */
  uReBlend: { value: number }
  // --- Zone reaction (shapes that ride the surface, owned by Scene/ZoneReaction) ---
  /** 1 = zone reaction active (localizes deformation to the moving shapes). */
  uZoneEnabled: { value: number }
  /** Number of live zones (0..MAX_ZONES). */
  uZoneCount: { value: number }
  /** Per-zone center on the unit sphere (surface direction). */
  uZoneCenter: { value: THREE.Vector3[] }
  /** Per-zone band index (0 bass, 1 mid, 2 treble, 3 level). */
  uZoneBand: { value: number[] }
  /** Per-zone shape (0 circle, 1 square, 2 triangle, 3 image). */
  uZoneShape: { value: number[] }
  /** Per-zone radius on the surface (angular, radians). */
  uZoneRadius: { value: number[] }
  /** Per-band onset pulse (0..1), drives the local deformation + shape glow. */
  uBandPulse: { value: number[] }
  /** Per-band image (used by shapes whose type is 'image'). */
  uZoneImage0: { value: THREE.Texture }
  uZoneImage1: { value: THREE.Texture }
  uZoneImage2: { value: THREE.Texture }
  uZoneImage3: { value: THREE.Texture }
  /** 1 if the matching band image is loaded. */
  uZoneImageActive: { value: number[] }
  /** 1 = draw the zone shapes; 0 = hide them (the local deformation still runs). */
  uZoneShowShapes: { value: number }
  [key: string]: { value: unknown }
}

const TEX_SIZE = 512

function makeDataTexture(fill: number): THREE.DataTexture {
  const data = new Uint8Array(TEX_SIZE).fill(fill)
  const tex = new THREE.DataTexture(data, TEX_SIZE, 1, THREE.RedFormat)
  tex.minFilter = THREE.LinearFilter
  tex.magFilter = THREE.LinearFilter
  tex.needsUpdate = true
  return tex
}

export function createFreqTexture(): THREE.DataTexture {
  return makeDataTexture(0)
}

export function createWaveTexture(): THREE.DataTexture {
  // Center 0.5 = silence (the waveform is remapped -1..1 -> 0..1).
  return makeDataTexture(128)
}

/** 1x1 black filler texture for the media sampler when there is no video. */
export function createDummyTexture(): THREE.DataTexture {
  const tex = new THREE.DataTexture(
    new Uint8Array([0, 0, 0, 255]),
    1,
    1,
    THREE.RGBAFormat,
  )
  tex.needsUpdate = true
  return tex
}

export function createSharedUniforms(
  freqTex: THREE.DataTexture,
  waveTex: THREE.DataTexture,
): SharedUniforms {
  return {
    uTime: { value: 0 },
    uEnergyTime: { value: 0 },
    uBeatPulse: { value: 0 },
    uBass: { value: 0 },
    uMid: { value: 0 },
    uTreble: { value: 0 },
    uLevel: { value: 0 },
    uBeat: { value: 0 },
    uHue: { value: 0.55 },
    uReactivity: { value: 1 },
    uBassPunch: { value: 1 },
    uSeed: { value: new THREE.Vector3(0, 0, 0) },
    uSeedPulse: { value: 0 },
    uPointScale: { value: 1 },
    uPointShape: { value: 0 },
    uPointSolid: { value: 0 },
    uDispScale: { value: 0.9 },
    uDispThreshold: { value: 0.5 },
    uDispAudio: { value: 1 },
    uDispBand: { value: 0 },
    uPixelRatio: { value: 1 },
    uFreq: { value: freqTex },
    uFreqSize: { value: TEX_SIZE },
    uWave: { value: waveTex },
    uWaveSize: { value: TEX_SIZE },
    uMedia: { value: createDummyTexture() },
    uMedia2: { value: createDummyTexture() },
    uLoopFade: { value: 0 },
    uMediaActive: { value: 0 },
    uMediaFull: { value: 0 },
    uMediaAspect: { value: 1 },
    uScreenAspect: { value: 1 },
    uTunnel: { value: 0 },
    uTunnelAngle: { value: 0 },
    uTunnelDepth: { value: 0 },
    uTunnelShift: { value: new THREE.Vector2(0, 0) },
    uMirror: { value: 0 },
    uMirrorAmount: { value: 2 },
    uMediaOpacity: { value: 1 },
    uTexturePrimitive: { value: 0 },
    uResolution: { value: new THREE.Vector2(1, 1) },
    uPrimitiveMedia: { value: createDummyTexture() },
    uPrimitiveMediaActive: { value: 0 },
    uPrimitiveAspect: { value: 1 },
    uReLUT: { value: createDummyTexture() },
    uReActive: { value: 0 },
    uReIntensity: { value: 1 },
    uReBlack: { value: 0 },
    uReWhite: { value: 1 },
    uReBlend: { value: 0 },
    uZoneEnabled: { value: 0 },
    uZoneCount: { value: 0 },
    uZoneCenter: { value: Array.from({ length: MAX_ZONES }, () => new THREE.Vector3(0, 1, 0)) },
    uZoneBand: { value: new Array(MAX_ZONES).fill(0) },
    uZoneShape: { value: new Array(MAX_ZONES).fill(0) },
    uZoneRadius: { value: new Array(MAX_ZONES).fill(0.3) },
    uBandPulse: { value: [0, 0, 0, 0] },
    uZoneImage0: { value: createDummyTexture() },
    uZoneImage1: { value: createDummyTexture() },
    uZoneImage2: { value: createDummyTexture() },
    uZoneImage3: { value: createDummyTexture() },
    uZoneImageActive: { value: [0, 0, 0, 0] },
    uZoneShowShapes: { value: 1 },
  }
}

/**
 * GLSL helper: when uTexturePrimitive is on and there's media, returns the media
 * sampled at the fragment's screen position ("cover" fit), otherwise the base
 * color. Lets a primitive's pixels show the video/image. Primitives that already
 * declare uMedia/uMediaActive (e.g. the LED panel) must NOT re-include the block.
 */
export const PRIMITIVE_TEXTURE_GLSL = /* glsl */ `
  uniform sampler2D uPrimitiveMedia;
  uniform float uPrimitiveMediaActive;
  uniform float uTexturePrimitive;
  uniform float uPrimitiveAspect;
  uniform float uScreenAspect;
  uniform vec2 uResolution;
  uniform sampler2D uReLUT;
  uniform float uReActive;
  uniform float uReIntensity;
  uniform float uReBlack;
  uniform float uReWhite;
  uniform float uReBlend;
  // Combine the source (base) with the palette color (top): 0 normal, 1 multiply,
  // 2 screen, 3 add.
  vec3 blendColor(vec3 base, vec3 top) {
    int m = int(uReBlend + 0.5);
    if (m == 1) return base * top;
    if (m == 2) return 1.0 - (1.0 - base) * (1.0 - top);
    if (m == 3) return min(vec3(1.0), base + top);
    return top;
  }
  // Recolor a color through the palette LUT. Reads the source as GRAYSCALE
  // (perceptual luminance) so the index depends only on brightness — the source's
  // original hues never skew which palette color a region gets. Matches the
  // background VideoLab so colored videos recolor cleanly to the palette.
  vec3 reColor(vec3 c) {
    float g = dot(c, vec3(0.299, 0.587, 0.114));
    g = clamp((g - uReBlack) / max(1e-4, uReWhite - uReBlack), 0.0, 1.0);
    vec3 mapped = texture2D(uReLUT, vec2(g, 0.5)).rgb;
    return mix(vec3(g), blendColor(vec3(g), mapped), uReIntensity);
  }
  vec3 applyPrimitiveTexture(vec3 baseColor) {
    // No texture: Colors on → recolor the primitive with the palette (by its
    // brightness); Colors off → the primitive's own color, untouched (keeps its
    // natural, audio-reactive look).
    if (uTexturePrimitive < 0.5 || uPrimitiveMediaActive < 0.5) {
      if (uReActive > 0.5) return reColor(baseColor);
      return baseColor;
    }
    vec2 uv = gl_FragCoord.xy / uResolution;
    if (uScreenAspect > uPrimitiveAspect) {
      uv.y = (uv.y - 0.5) * (uPrimitiveAspect / uScreenAspect) + 0.5;
    } else {
      uv.x = (uv.x - 0.5) * (uScreenAspect / uPrimitiveAspect) + 0.5;
    }
    vec3 c = texture2D(uPrimitiveMedia, uv).rgb;
    // Recolor the texture with the palette when Colors is on.
    return uReActive > 0.5 ? reColor(c) : c;
  }
`

/** Updates the uniforms and textures (spectrum + waveform) with the audio frame. */
export function updateUniforms(
  u: SharedUniforms,
  freqTex: THREE.DataTexture,
  waveTex: THREE.DataTexture,
  frame: AudioFrame,
  params: VisualParams,
  time: number,
) {
  u.uTime.value = time
  u.uBass.value = frame.bass
  u.uMid.value = frame.mid
  u.uTreble.value = frame.treble
  u.uLevel.value = frame.level
  u.uBeat.value = frame.beatIntensity
  u.uHue.value = params.hue
  u.uReactivity.value = params.reactivity
  u.uBassPunch.value = params.bassPunch
  u.uPointScale.value = params.pointSize
  u.uPointShape.value = params.pointShape === 'square' ? 1 : params.pointShape === 'triangle' ? 2 : 0
  u.uPointSolid.value = params.pointSolid ? 1 : 0
  // Video displacement field params. Guarded so a stale uniforms object (e.g. after
  // a partial HMR update) can't throw here and kill the whole render loop.
  if (u.uDispScale) u.uDispScale.value = params.displaceScale ?? 0.9
  if (u.uDispThreshold) u.uDispThreshold.value = params.displaceThreshold ?? 0.5
  if (u.uDispAudio) u.uDispAudio.value = params.displaceAudio ?? 1
  if (u.uDispBand)
    u.uDispBand.value =
      params.displaceBand === 'bass'
        ? 1
        : params.displaceBand === 'mid'
          ? 2
          : params.displaceBand === 'treble'
            ? 3
            : 0
  // uSeed / uSeedPulse are driven by Scene (random-deform feature), not here.

  // Resamples the spectrum to the texture width.
  const fsrc = frame.frequencies
  const fdst = freqTex.image.data as Uint8Array
  const n = fdst.length
  if (fsrc.length > 0) {
    for (let i = 0; i < n; i++) {
      const idx = Math.floor((i / n) * fsrc.length)
      fdst[i] = Math.min(255, Math.floor(fsrc[idx] * 255))
    }
    freqTex.needsUpdate = true
  }

  // Resamples the waveform (-1..1 -> 0..255 with center at 128).
  const wsrc = frame.waveform
  const wdst = waveTex.image.data as Uint8Array
  if (wsrc.length > 0) {
    for (let i = 0; i < n; i++) {
      const idx = Math.floor((i / n) * wsrc.length)
      const v = Math.max(-1, Math.min(1, wsrc[idx]))
      wdst[i] = Math.floor((v * 0.5 + 0.5) * 255)
    }
    waveTex.needsUpdate = true
  }
}
